import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  ROLE_OPTIONS, SIZE_OPTIONS, REVENUE_OPTIONS_ES, REVENUE_OPTIONS_AR,
  PHONE_COUNTRIES, TOOL_CATALOG, TOOL_DOMAINS, buildLogoUrl,
  PAIN_SUGGESTIONS_ES, PAIN_SUGGESTIONS_AR, navigateTo,
  SIGNAL_CATALOG, VOLUME_BANDS, LEVER_OPTIONS
} from './shared.js';

/* ---------- COPY ---------- */
function getCopy(dialect) {
  const ar = dialect === 'rioplatense';
  return {
    metaTitle: ar ? 'Reservá tu diagnóstico — EnAutomático' : 'Reserva tu diagnóstico — EnAutomático',
    stepper: {
      labels: ['Datos', 'Problemas', 'Stack', 'Informe', 'Hora'],
      stepOf: ar ? 'Paso {n} de 5' : 'Paso {n} de 5'
    },
    datos: {
      label: 'Datos',
      title: ar
        ? <>Cuéntanos sobre <em>tu negocio.</em></>
        : <>Cuéntanos sobre <em>tu negocio.</em></>,
      lead: ar
        ? 'Diez campos. Sofía los lee antes de marcar tu número.'
        : 'Diez campos. Sofía los lee antes de marcar tu número.',
      painTitle: ar
        ? <>Escribí una lista de problemas que tenga tu negocio <em>y ordenalo por prioridad.</em></>
        : <>Escribe una lista de problemas que tenga tu negocio <em>y ordénalo por prioridad.</em></>,
      fields: {
        name: 'Nombre y apellido',
        email: 'Email',
        phone: 'Teléfono',
        role: 'Tu rol',
        company: 'Empresa',
        website: 'Sitio web',
        ciudad: 'Ciudad',
        sector: 'Sector o industria',
        size: 'Tamaño del equipo',
        revenue: 'Facturación anual aproximada'
      },
      placeholders: {
        name: 'Laura Martín',
        email: 'laura@empresa.com',
        company: 'Llorens & Vidal',
        website: 'empresa.com',
        ciudad: 'Barcelona',
        sector: 'Consultoría, agencia, despacho',
        phone: 'número local'
      },
      hints: {
        phone: 'Sofía te llama a este número',
        website: 'Opcional, ayuda al diagnóstico',
        revenue: 'Solo Sofía lo ve. Nada se publica'
      },
      errors: {
        name: 'Nombre requerido',
        email: 'Email inválido',
        phone: 'Teléfono incompleto',
        company: 'Empresa requerida',
        role: 'Elige tu rol',
        ciudad: 'Ciudad requerida',
        sector: 'Sector requerido',
        size: 'Elige el tamaño',
        revenueRange: 'Elige un rango'
      },
      pain: {
        suggestionLabel: 'Sugerencia',
        addLabel: 'Agregar a la lista',
        rejectLabel: 'Probar otro',
        customPlaceholder: ar
          ? 'O escribí en qué creés que podrías mejorar y presioná Enter'
          : 'O escribe en qué crees que podrías mejorar y presiona Enter',
        capturedTitle: 'Lista de problemas',
        capturedSub: ar ? 'Arrastrá para reordenar' : 'Arrastra para reordenar',
        empty: ar ? 'Aún ninguno. Empezá por el más urgente.' : 'Aún ninguno. Empieza por el más urgente.',
        exhausted: ar ? 'No quedan más sugerencias. Sumá los tuyos abajo.' : 'No quedan más sugerencias. Suma los tuyos abajo.'
      },
      selectPlaceholder: ar ? 'Elegí una opción' : 'Elige una opción',
      missingFields: ar ? 'Faltan {n} campos' : 'Faltan {n} campos',
      allComplete: ar ? 'Todo listo' : 'Todo listo',
      ctaNext: 'Continuar a Problemas'
    },
    problemas: {
      label: 'Problemas',
      title: ar
        ? <>Escribí una lista de problemas que tenga tu negocio <em>y ordenalo por prioridad.</em></>
        : <>Escribe una lista de problemas que tenga tu negocio <em>y ordénalo por prioridad.</em></>,
      leverQuestion: ar
        ? 'Si la IA pudiera arreglar UNA sola cosa en tu negocio, ¿qué preferirías?'
        : 'Si la IA pudiera arreglar UNA sola cosa en tu negocio, ¿qué preferirías?',
      leverRequired: '*',
      ctaBack: 'Volver a Datos',
      ctaNext: 'Continuar a Stack'
    },
    stack: {
      label: 'Stack',
      title: ar ? <>Las herramientas que <em>ya tenés encendidas.</em></> : <>Las herramientas que <em>ya tienes encendidas.</em></>,
      lead: ar
        ? 'Marcá las que ya usás. No tiene que ser perfecto.'
        : 'Marca las que ya usas. No tiene que ser perfecto.',
      search: 'Buscar una herramienta',
      counterOne: 'herramienta marcada',
      counterMany: 'herramientas marcadas',
      customLabel: ar ? 'Añadir tu herramienta' : 'Añadir tu herramienta',
      customSub: ar ? 'Si no la encontrás abajo, sumala acá.' : 'Si no la encuentras abajo, súmala aquí.',
      customPlaceholder: 'Ej: ManyChat, Telegram Bot, Excel macro',
      customAdd: ar ? 'Agregar' : 'Añadir',
      ctaBack: 'Volver a Datos',
      ctaNext: ar ? 'Continuar a Hora' : 'Continuar a Hora'
    },
    hora: {
      label: 'Hora',
      title: ar ? <>Elegí cuándo te llama <em>Sofía.</em></> : <>Elige cuándo te llama <em>Sofía.</em></>,
      lead: ar
        ? 'Veinte minutos. Castellano o rioplatense, ya lo sabe.'
        : 'Veinte minutos. Castellano o rioplatense, ya lo sabe.',
      stubFor: 'Para',
      stubStack: 'Stack',
      stubDialect: 'Idioma',
      stubPlaza: 'Plaza',
      stubPlazaPilot: 'Piloto · gratis',
      fallbackTitle: ar ? 'No podemos cargar el calendario aquí' : 'No podemos cargar el calendario aquí',
      fallbackBody: ar
        ? 'Suele pasar por cookies de terceros bloqueadas. Abrilo en una pestaña nueva o copiá el enlace.'
        : 'Suele pasar por cookies de terceros bloqueadas. Ábrelo en una pestaña nueva o copia el enlace.',
      ctaBack: 'Volver a Stack'
    },
    chrome: {
      backHome: ar ? 'Volver al inicio' : 'Volver al inicio',
      shareWhatsApp: 'Compartir',
      linkCopied: 'Enlace copiado',
      returning: ar ? 'Continuamos donde lo dejamos.' : 'Continuamos donde lo dejamos.',
      returningReset: ar ? 'Empezar de nuevo' : 'Empezar de nuevo'
    },
    confirmado: {
      stamp: 'CONFIRMADA',
      title: 'Reserva confirmada.',
      body: ar
        ? 'Recibirás un correo con el horario y un enlace para la llamada. Sofía te llama desde un número Telnyx en España (+34). Si tu zona horaria es de Argentina, también podemos coordinar a tu hora local.'
        : 'Recibirás un correo con el horario y un enlace para la llamada. Sofía te llama desde un número Telnyx en España (+34).',
      next: 'Qué pasa después',
      step1: ar
        ? 'Veinte minutos al teléfono con Sofía. Te hace preguntas concretas, sin guion robótico.'
        : 'Veinte minutos al teléfono con Sofía. Te hace preguntas concretas, sin guion robótico.',
      step2: ar
        ? 'Cuarenta y ocho horas después, recibís un informe de 8 slides con quick wins, herramientas y plan de 4 días.'
        : 'Cuarenta y ocho horas después, recibes un informe de 8 slides con quick wins, herramientas y plan de 4 días.',
      step3: ar
        ? 'Si querés, una segunda llamada de 30 minutos con Santiago para revisarlo en pantalla.'
        : 'Si quieres, una segunda llamada de 30 minutos con Santiago para revisarlo en pantalla.',
      backHome: ar ? 'Volver al inicio' : 'Volver al inicio',
      bookAgain: ar ? 'Reservar otra llamada' : 'Reservar otra llamada'
    },
    teaser: {
      label: 'Informe',
      title: ar
        ? <>Tu informe <em>preliminar.</em></>
        : <>Tu informe <em>preliminar.</em></>,
      lead: ar
        ? 'Generado en segundos. Sofía lo valida y profundiza en la llamada.'
        : 'Generado en segundos. Sofía lo valida y profundiza en la llamada.',
      aiDisclosure: 'Este informe se generó automáticamente con IA. La llamada con Sofía valida y profundiza estas estimaciones.',
      loading: 'Generando tu informe…',
      readyTitle: 'Tu informe está listo',
      openReport: ar ? 'Abrí tu informe en una pestaña nueva →' : 'Abrir tu informe en una pestaña nueva →',
      disclosure: 'Vista previa al instante · plan completo tras llamada con Sofía',
      hoursLabel: 'horas recuperadas',
      eurosLabel: ar ? 'ahorrás en margen' : 'ahorras en margen',
      opportunitiesLabel: ar ? 'oportunidades identificadas' : 'oportunidades identificadas',
      error: ar
        ? 'No pudimos generar tu informe ahora. Reservá tu llamada y Sofía lo cubre todo en directo.'
        : 'No pudimos generar tu informe ahora. Reserva tu llamada y Sofía lo cubre todo en directo.',
      continue: ar ? 'Continuá con tu reserva' : 'Continúa con tu reserva',
      back: 'Volver',
      iframeTitle: 'Tu diagnóstico estimado'
    }
  };
}

/* ---------- VALIDATION ---------- */
const REQUIRED_KEYS = ['name', 'email', 'phone', 'company', 'role', 'ciudad', 'sector', 'size', 'revenueRange'];

function validateField(key, val) {
  switch (key) {
    case 'name': return !val.trim() ? 'name' : '';
    case 'email': return !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val) ? 'email' : '';
    case 'phone': {
      const d = (val || '').replace(/\D/g, '');
      if (!val.startsWith('+') || d.length < 8 || d.length > 15) return 'phone';
      return '';
    }
    case 'company': return !val.trim() ? 'company' : '';
    case 'role': return !val ? 'role' : '';
    case 'ciudad': return !val.trim() ? 'ciudad' : '';
    case 'sector': return !val.trim() ? 'sector' : '';
    case 'size': return !val ? 'size' : '';
    case 'revenueRange': return !val ? 'revenueRange' : '';
    default: return '';
  }
}

function getMissingFields(form) {
  return REQUIRED_KEYS.filter(k => validateField(k, form[k] || ''));
}

/* ---------- LOCAL STORAGE ---------- */
const DRAFT_KEY = 'ea_diagnostic_draft';
const DRAFT_TTL_DAYS = 7;

function loadDraft() {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.form) return null;
    const ageDays = (Date.now() - (parsed.savedAt || 0)) / 86400000;
    if (ageDays > DRAFT_TTL_DAYS) {
      window.localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch (e) { return null; }
}

function saveDraft(form, step) {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    const prev = raw ? (JSON.parse(raw) || {}) : {};
    // Preserve any teaser cache that was previously saved.
    const next = { ...prev, form, step, savedAt: Date.now() };
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
  } catch (e) { /* storage blocked, ignore */ }
}

function clearDraft() {
  try { window.localStorage.removeItem(DRAFT_KEY); } catch (e) {}
}

// Stable cache key for the teaser response. Includes fields that
// meaningfully affect the Gemini prompt OR the Notion upsert keying:
//   - sector / ciudad / size / revenueRange / tools / mainPain / dialect → Gemini prompt
//   - website → scraped + summarized + fed into prompt (Chunk 2 onward)
//   - email → Notion upsert key (changing email = different CRM row)
//   - name / phone → captured in Notion row + recovery email (typo fix → regenerate)
// Phone country, customTool input-box state etc. don't affect generation.
function teaserCacheKey(form, dialect) {
  const sortedTools = Array.isArray(form.tools) ? [...form.tools].sort() : [];
  const sortedSignals = Array.isArray(form.signals) ? [...form.signals].sort() : [];
  return JSON.stringify({
    email: (form.email || '').trim().toLowerCase(),
    name: (form.name || '').trim(),
    phone: (form.phone || '').trim(),
    sector: (form.sector || '').trim(),
    ciudad: (form.ciudad || '').trim(),
    role: (form.role || '').trim(),
    company: (form.company || '').trim(),
    size: (form.size || '').trim(),
    revenueRange: (form.revenueRange || '').trim(),
    tools: sortedTools,
    mainPain: (form.mainPain || '').trim(),
    dialect: dialect || 'castellano',
    website: (form.website || '').trim().toLowerCase(),  // NEW (Chunk 2)
    signals: sortedSignals,
    volumes: form.volumes || {},
    lever: (form.lever || '').trim(),
  });
}

function saveTeaserCache(meta, cacheKey) {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    const prev = raw ? (JSON.parse(raw) || {}) : {};
    prev.teaser = {
      slug: meta.slug,
      hours_saved: meta.hours_saved,
      roi_eur: meta.roi_eur,
      opportunities_count: meta.opportunities_count,
      cacheKey,
      savedAt: Date.now(),
    };
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(prev));
  } catch (e) { /* storage blocked, ignore */ }
}

function loadTeaserCache(cacheKey) {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.teaser || parsed.teaser.cacheKey !== cacheKey) return null;
    // Guard: pre-spec-#1 cache entries stored teaser_html but no slug → treat as miss.
    if (!parsed.teaser.slug) return null;
    // Same TTL as draft (7d) — beyond that, regenerate fresh.
    const ageDays = (Date.now() - (parsed.teaser.savedAt || 0)) / 86400000;
    if (ageDays > DRAFT_TTL_DAYS) return null;
    return parsed.teaser;
  } catch (e) { return null; }
}

/* ---------- ICONS ---------- */
function IconLock({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="1.5" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
function IconCheck({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}
function IconArrow({ size = 14, dir = 'right' }) {
  if (dir === 'left') {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
        <path d="M11 7H3M7 3 3 7l4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
      <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconWhatsApp({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.05 4.91A10 10 0 0 0 4.16 18.6L3 22l3.5-1.13A10 10 0 1 0 19.05 4.91Zm-7.05 16.1a8.3 8.3 0 0 1-4.23-1.16l-.3-.18-2.46.79.8-2.4-.2-.31A8.3 8.3 0 1 1 12 21.01Zm4.55-6.21c-.25-.13-1.47-.72-1.7-.81-.23-.08-.4-.13-.56.13-.17.25-.65.81-.79.97-.15.17-.29.18-.54.06-.25-.12-1.05-.39-2-1.24-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.39.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.13-.56-1.34-.76-1.83-.2-.49-.41-.42-.56-.43h-.48a.92.92 0 0 0-.67.31c-.23.25-.88.86-.88 2.09 0 1.23.9 2.43 1.03 2.6.13.17 1.78 2.71 4.32 3.8.6.26 1.07.42 1.44.54.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.23-.17-.48-.29Z" />
    </svg>
  );
}
function IconShuffle({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h4l4 6-4 6H3" />
      <path d="M21 6h-4l-4 6 4 6h4" />
      <path d="M17 4l4 2-4 2" />
      <path d="M17 16l4 2-4 2" />
    </svg>
  );
}
function IconPlus({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/* ---------- TOP BAR ---------- */
function TopBar({ copy }) {
  return (
    <header className="diag-topbar">
      <a href="/" onClick={(e) => { e.preventDefault(); navigateTo('/'); }} className="diag-back" aria-label={copy.chrome.backHome}>
        <IconArrow size={12} dir="left" />
        <span className="diag-back-label">EnAutomático</span>
      </a>
    </header>
  );
}

/* ---------- STEPPER (horizontal, replaces hero + spine) ---------- */
function Stepper({ currentStep, datosValid, copy, onStepClick }) {
  const items = [
    { k: 'datos', label: copy.stepper.labels[0] },
    { k: 'problemas', label: copy.stepper.labels[1] },
    { k: 'stack', label: copy.stepper.labels[2] },
    { k: 'teaser', label: copy.stepper.labels[3] },
    { k: 'hora', label: copy.stepper.labels[4] }
  ];
  function statusFor(idx) {
    if (idx < currentStep) return 'done';
    if (idx === currentStep) return 'active';
    // Stack and Hora are both gated on datos being complete — datos is the
    // gate, not just the final-confirmation step.
    if (idx > 0 && !datosValid) return 'locked';
    return 'pending';
  }
  function canClick(idx) {
    if (idx === currentStep) return false;
    if (idx < currentStep) return true;
    if (idx > 0 && !datosValid) return false;
    return true;
  }
  return (
    <nav className="diag-stepper" aria-label="Progreso de la reserva">
      <div className="diag-stepper-inner">
        <ol className="diag-stepper-track">
          {items.map((it, i) => {
            const s = statusFor(i);
            return (
              <li key={it.k} className={`diag-stepper-step is-${s}`}>
                <button
                  type="button"
                  onClick={() => canClick(i) && onStepClick(i)}
                  disabled={!canClick(i)}
                  aria-current={i === currentStep ? 'step' : undefined}
                  className="diag-stepper-btn"
                >
                  <span className="diag-stepper-dot" aria-hidden="true">
                    {s === 'done' ? <IconCheck size={11} /> :
                     s === 'locked' ? <IconLock size={10} /> :
                     <span className="diag-stepper-dot-num">{i + 1}</span>}
                  </span>
                  <span className="diag-stepper-label">{it.label}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

/* ---------- DATOS STEP ---------- */
function DatosFields({ form, setForm, touched, setTouched, attempted, copy, dialect }) {
  const phoneDropdownRef = useRef(null);
  const [phoneCountryOpen, setPhoneCountryOpen] = useState(false);

  useEffect(() => {
    if (!phoneCountryOpen) return;
    const onClick = (e) => {
      if (phoneDropdownRef.current && !phoneDropdownRef.current.contains(e.target)) {
        setPhoneCountryOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setPhoneCountryOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [phoneCountryOpen]);

  const revenueOpts = dialect === 'rioplatense' ? REVENUE_OPTIONS_AR : REVENUE_OPTIONS_ES;
  const fields = [
    { key: 'name', label: copy.datos.fields.name, placeholder: copy.datos.placeholders.name, type: 'text', req: true },
    { key: 'email', label: copy.datos.fields.email, placeholder: copy.datos.placeholders.email, type: 'email', req: true },
    { key: 'phone', label: copy.datos.fields.phone, type: 'tel', req: true },
    { key: 'role', label: copy.datos.fields.role, type: 'select', options: ROLE_OPTIONS, req: true },
    { key: 'company', label: copy.datos.fields.company, placeholder: copy.datos.placeholders.company, type: 'text', req: true },
    { key: 'website', label: copy.datos.fields.website, placeholder: copy.datos.placeholders.website, type: 'text', req: false, hint: copy.datos.hints.website },
    { key: 'ciudad', label: copy.datos.fields.ciudad, placeholder: copy.datos.placeholders.ciudad, type: 'text', req: true },
    { key: 'sector', label: copy.datos.fields.sector, placeholder: copy.datos.placeholders.sector, type: 'text', req: true },
    { key: 'size', label: copy.datos.fields.size, type: 'select', options: SIZE_OPTIONS, req: true },
    { key: 'revenueRange', label: copy.datos.fields.revenue, type: 'select', options: revenueOpts, req: true, hint: copy.datos.hints.revenue }
  ];

  function shouldShowError(key) {
    if (!REQUIRED_KEYS.includes(key)) return false;
    if (!touched[key] && !attempted) return false;
    return !!validateField(key, form[key] || '');
  }
  function fieldError(key) {
    if (!shouldShowError(key)) return '';
    return copy.datos.errors[key] || '';
  }

  return (
    <div className="diag-fieldgrid">
      {fields.map(f => {
        if (f.key === 'phone') {
          const selected = PHONE_COUNTRIES.find(c => c.code === form.phoneCountry) || PHONE_COUNTRIES[0];
          const err = fieldError('phone');
          return (
            <div key={f.key} className={`diag-field ${err ? 'has-error' : ''}`}>
              <label className="diag-field-label mono">{f.label} {f.req && <span className="req">*</span>}</label>
              <div ref={phoneDropdownRef} className="diag-phone">
                <div className={`diag-phone-row ${err ? 'has-error' : ''}`}>
                  <button
                    type="button"
                    onClick={() => setPhoneCountryOpen(o => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={phoneCountryOpen}
                    aria-label="Código de país"
                    className="diag-phone-flag"
                  >
                    <span className={`fi fi-${selected.code.toLowerCase()}`} style={{ display: 'inline-block', width: '1.4em', height: '1em', borderRadius: 2 }} />
                    <span className="diag-phone-caret">▾</span>
                  </button>
                  <input
                    type="tel"
                    placeholder={copy.datos.placeholders.phone}
                    value={form.phone}
                    maxLength={16}
                    onBlur={() => setTouched(t => ({ ...t, phone: true }))}
                    onChange={(e) => {
                      let v = e.target.value;
                      const hasLeadingPlus = v.trimStart().startsWith('+');
                      v = v.replace(/\D/g, '');
                      if (hasLeadingPlus) v = '+' + v;
                      if (v.length > 16) v = v.slice(0, 16);
                      setForm({ ...form, phone: v });
                    }}
                    className="diag-phone-input"
                  />
                </div>
                {phoneCountryOpen && (
                  <ul role="listbox" aria-label="Países" className="diag-phone-list">
                    {PHONE_COUNTRIES.map(c => (
                      <li
                        key={c.code}
                        role="option"
                        aria-selected={c.code === form.phoneCountry}
                        onClick={() => {
                          const oldDial = (PHONE_COUNTRIES.find(p => p.code === form.phoneCountry) || PHONE_COUNTRIES[0]).dial;
                          const newDial = c.dial;
                          let p = form.phone || '';
                          if (p.startsWith(oldDial)) {
                            p = newDial + p.slice(oldDial.length);
                          } else if (!p.startsWith(newDial)) {
                            p = newDial + p;
                          }
                          setForm({ ...form, phoneCountry: c.code, phone: p });
                          setPhoneCountryOpen(false);
                        }}
                        className={c.code === form.phoneCountry ? 'is-selected' : ''}
                      >
                        <span className={`fi fi-${c.code.toLowerCase()}`} style={{ width: '1.4em', height: '1em', borderRadius: 2, flexShrink: 0 }} />
                        <span className="dial mono">{c.dial}</span>
                        <span className="cname">{c.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className={`diag-field-hint mono ${err ? 'is-err' : ''}`}>{err || copy.datos.hints.phone}</div>
            </div>
          );
        }
        const err = fieldError(f.key);
        return (
          <div key={f.key} className={`diag-field ${err ? 'has-error' : ''}`}>
            <label className="diag-field-label mono">{f.label} {f.req && <span className="req">*</span>}</label>
            {f.type === 'select' ? (
              <select
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                onBlur={() => setTouched(t => ({ ...t, [f.key]: true }))}
                className={`diag-input ${err ? 'has-error' : ''}`}
              >
                <option value="">({copy.datos.selectPlaceholder})</option>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                type={f.type}
                placeholder={f.placeholder}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                onBlur={() => setTouched(t => ({ ...t, [f.key]: true }))}
                className={`diag-input ${err ? 'has-error' : ''}`}
              />
            )}
            {err ? (
              <div className="diag-field-hint mono is-err">{err}</div>
            ) : f.hint ? (
              <div className="diag-field-hint mono">{f.hint}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- SIGNAL CARD + VOLUME PROBE ---------- */
function SignalCard({ signal, checked, onToggle, dialect, children }) {
  const label = dialect === 'rioplatense' ? signal.rioplatense : signal.castellano;
  return (
    <div className={`signal-card ${checked ? 'is-checked' : ''}`}>
      <label className="signal-card-head">
        <input type="checkbox" checked={checked} onChange={() => onToggle(signal.id)} />
        <span>{label}</span>
      </label>
      {checked ? <div className="signal-card-body">{children}</div> : null}
    </div>
  );
}

function VolumeProbe({ signal, value, onChange, dialect }) {
  const bands = VOLUME_BANDS[signal.id];
  if (!bands) return null; // consolidation has no probe
  const probeLabel = dialect === 'rioplatense' ? signal.probe?.rioplatense : signal.probe?.castellano;
  return (
    <div className="volume-probe">
      <div className="volume-probe-label">{probeLabel}</div>
      <div className="volume-probe-bands">
        {bands.bands.map((b, i) => (
          <button
            key={b}
            type="button"
            className={`volume-band ${value === b ? 'is-selected' : ''}`}
            onClick={() => onChange(signal.id, b)}
          >
            {bands.labels[i]}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- PROBLEMAS STEP ---------- */
function ProblemasStep({ form, setForm, copy, dialect, onBack, onContinue }) {
  const [currentSuggestion, setCurrentSuggestion] = useState(null);
  const [customInput, setCustomInput] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [rejectedSuggestions, setRejectedSuggestions] = useState(new Set());

  useEffect(() => {
    const suggestions = dialect === 'rioplatense' ? PAIN_SUGGESTIONS_AR : PAIN_SUGGESTIONS_ES;
    const addedPains = (form.mainPain || '').split('\n').filter(p => p.trim());
    let next = suggestions[Math.floor(Math.random() * suggestions.length)];
    let tries = 0;
    while (addedPains.includes(next) && tries < 10) {
      next = suggestions[Math.floor(Math.random() * suggestions.length)];
      tries++;
    }
    setCurrentSuggestion(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialect]);

  function getNextFreshSuggestion(currentPains) {
    const suggestions = dialect === 'rioplatense' ? PAIN_SUGGESTIONS_AR : PAIN_SUGGESTIONS_ES;
    const addedSet = new Set((currentPains || '').split('\n').filter(p => p.trim()).map(p => p.trim().toLowerCase()));
    const avail = suggestions.filter(s => !addedSet.has(s.trim().toLowerCase()) && !rejectedSuggestions.has(s.trim().toLowerCase()));
    if (avail.length === 0) return null;
    return avail[Math.floor(Math.random() * avail.length)];
  }

  function addPainToList(pain) {
    if (!pain || !pain.trim()) return;
    const next = form.mainPain ? form.mainPain + '\n' + pain.trim() : pain.trim();
    setForm({ ...form, mainPain: next });
    setCustomInput('');
    setCurrentSuggestion(getNextFreshSuggestion(next));
  }

  const signals = form.signals || [];
  const volumes = form.volumes || {};

  function toggleSignal(id) {
    if (signals.includes(id)) {
      const nextSignals = signals.filter(s => s !== id);
      const nextVolumes = { ...volumes };
      delete nextVolumes[id];
      setForm({ ...form, signals: nextSignals, volumes: nextVolumes });
    } else {
      setForm({ ...form, signals: [...signals, id] });
    }
  }

  function setVolume(id, band) {
    setForm({ ...form, volumes: { ...volumes, [id]: band } });
  }

  const allPains = (form.mainPain || '').split('\n').filter(p => p.trim());
  // Lever is required AND at least one signal or pain must be present.
  const canContinue = !!form.lever && (signals.length > 0 || allPains.length > 0);

  return (
    <section className="diag-step diag-step-problemas" aria-labelledby="diag-problemas-heading">
      <header className="diag-step-head">
        <div className="diag-step-headrow">
          <h1 id="diag-problemas-heading" className="diag-step-title serif">{copy.problemas.title}</h1>
        </div>
      </header>
      <div className="diag-step-body diag-problemas-body">

        {/* Forking question — lever pick (Q29). Required before advancing. */}
        <div className="signal-grid-section">
          <div className="diag-section-label">
            {copy.problemas.leverQuestion}
            {' '}<span className="req">{copy.problemas.leverRequired}</span>
          </div>
          <div className="diag-lever-group" role="radiogroup" aria-label={copy.problemas.leverQuestion}>
            {LEVER_OPTIONS.map(opt => {
              const label = dialect === 'rioplatense' ? opt.rioplatense : opt.castellano;
              const checked = form.lever === opt.value;
              return (
                <label key={opt.value} className={`diag-lever-option${checked ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="lever"
                    value={opt.value}
                    checked={checked}
                    onChange={() => setForm({ ...form, lever: opt.value })}
                    className="diag-lever-radio"
                  />
                  <span className="diag-lever-label">{label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Signal grid — structured section below lever */}
        <div className="signal-grid-section">
          <div className="diag-section-label">Marca dónde se va el tiempo</div>
          <div className="signal-grid">
            {SIGNAL_CATALOG.map(s => (
              <SignalCard
                key={s.id}
                signal={s}
                checked={signals.includes(s.id)}
                onToggle={toggleSignal}
                dialect={dialect}
              >
                <VolumeProbe
                  signal={s}
                  value={volumes[s.id]}
                  onChange={setVolume}
                  dialect={dialect}
                />
              </SignalCard>
            ))}
          </div>
        </div>

        {/* mainPain free-text section — preserved verbatim from previous implementation */}
        <div className="diag-problemas-input-col">
          <div className="diag-pain-custom">
            <textarea
              placeholder={copy.datos.pain.customPlaceholder}
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addPainToList(customInput); } }}
              className="diag-pain-textarea"
              rows={2}
            />
            <button
              type="button"
              onClick={() => addPainToList(customInput)}
              disabled={!customInput.trim()}
              className="diag-pain-add"
            >
              <IconPlus size={14} />
              <span>{copy.datos.pain.addLabel}</span>
            </button>
          </div>

          {currentSuggestion ? (
            <div className="diag-pain-card">
              <div className="diag-pain-stamp mono">{copy.datos.pain.suggestionLabel}</div>
              <p className="diag-pain-text">{currentSuggestion}</p>
              <div className="diag-pain-actions">
                <button
                  type="button"
                  onClick={() => {
                    setRejectedSuggestions(new Set([...rejectedSuggestions, currentSuggestion.trim().toLowerCase()]));
                    setCurrentSuggestion(getNextFreshSuggestion(form.mainPain));
                  }}
                  className="diag-pain-btn diag-pain-btn-reject"
                  aria-label={copy.datos.pain.rejectLabel}
                  title={copy.datos.pain.rejectLabel}
                >
                  <IconShuffle size={16} />
                  <span>{copy.datos.pain.rejectLabel}</span>
                </button>
                <button
                  type="button"
                  onClick={() => addPainToList(currentSuggestion)}
                  className="diag-pain-btn diag-pain-btn-accept"
                  aria-label={copy.datos.pain.addLabel}
                  title={copy.datos.pain.addLabel}
                >
                  <IconPlus size={18} />
                  <span>{copy.datos.pain.addLabel}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="diag-pain-exhausted mono">{copy.datos.pain.exhausted}</div>
          )}
        </div>

        <div className="diag-problemas-list-col">
          <div className="diag-pain-list">
            <div className="diag-pain-list-head mono">
              <span>{copy.datos.pain.capturedTitle} ({allPains.length})</span>
              {allPains.length > 0 ? <span className="diag-pain-list-sub">{copy.datos.pain.capturedSub}</span> : null}
            </div>
            {allPains.length === 0 ? (
              <p className="diag-pain-empty">{copy.datos.pain.empty}</p>
            ) : (
              allPains.map((pain, i) => {
                const isDragging = draggedIndex === i;
                return (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => setDraggedIndex(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (draggedIndex === null || draggedIndex === i) return;
                      const next = [...allPains];
                      const item = next[draggedIndex];
                      next.splice(draggedIndex, 1);
                      next.splice(i, 0, item);
                      setForm({ ...form, mainPain: next.join('\n') });
                      setDraggedIndex(null);
                    }}
                    onDragEnd={() => setDraggedIndex(null)}
                    className={`diag-pain-item ${isDragging ? 'is-dragging' : ''}`}
                  >
                    <span className="diag-pain-handle mono" aria-hidden="true">≡</span>
                    <span className="diag-pain-num mono">{String(i + 1).padStart(2, '0')}</span>
                    <span className="diag-pain-content">{pain}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const next = allPains.filter((_, idx) => idx !== i);
                        setForm({ ...form, mainPain: next.join('\n') });
                      }}
                      className="diag-pain-remove"
                      aria-label="Eliminar"
                      title="Eliminar"
                    >×</button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      <footer className="diag-step-actions">
        <button type="button" onClick={onBack} className="diag-cta-ghost">
          <IconArrow size={12} dir="left" />
          <span>{copy.problemas.ctaBack}</span>
        </button>
        <button type="button" onClick={onContinue} disabled={!canContinue} className="diag-cta-primary">
          <span>{copy.problemas.ctaNext}</span>
          <IconArrow size={12} />
        </button>
      </footer>
    </section>
  );
}

function DatosStep({ form, setForm, touched, setTouched, attempted, setAttempted, copy, dialect, onContinue, missingCount, formValid }) {
  function handleContinue() {
    if (!formValid) {
      setAttempted(true);
      const missing = getMissingFields(form);
      requestAnimationFrame(() => {
        const first = missing[0];
        if (!first) return;
        const el = document.querySelector(`.diag-field.has-error input, .diag-field.has-error select`);
        if (el && typeof el.focus === 'function') {
          el.focus();
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      return;
    }
    onContinue();
  }
  return (
    <section className="diag-step diag-step-datos" aria-labelledby="diag-datos-heading">
      <header className="diag-step-head">
        <div className="diag-step-headrow">
          <h1 id="diag-datos-heading" className="diag-step-title serif">{copy.datos.title}</h1>
        </div>
      </header>
      <div className="diag-step-body diag-datos-body">
        <DatosFields
          form={form}
          setForm={setForm}
          touched={touched}
          setTouched={setTouched}
          attempted={attempted}
          copy={copy}
          dialect={dialect}
        />
      </div>
      <footer className="diag-step-actions">
        <span className={`diag-step-status mono ${formValid ? 'is-ready' : ''}`}>
          {formValid ? copy.datos.allComplete : copy.datos.missingFields.replace('{n}', missingCount)}
        </span>
        <div className="diag-step-actions-buttons">
          <button
            type="button"
            onClick={handleContinue}
            className="diag-cta-primary"
            disabled={false}
          >
            <span>{copy.datos.ctaNext}</span>
            <IconArrow size={12} />
          </button>
        </div>
      </footer>
    </section>
  );
}

/* ---------- STACK STEP ---------- */
function StackStep({ form, setForm, copy, onBack, onContinue }) {
  const [toolSearch, setToolSearch] = useState('');

  const toggleTool = (tool) =>
    setForm({ ...form, tools: form.tools.includes(tool) ? form.tools.filter(t => t !== tool) : [...form.tools, tool] });

  const addCustomTool = () => {
    const v = (form.customTool || '').trim();
    if (!v) return;
    if (form.tools.includes(v)) { setForm({ ...form, customTool: '' }); return; }
    setForm({ ...form, tools: [...form.tools, v], customTool: '' });
  };

  const counter = form.tools.length;
  const counterLabel = counter === 1 ? copy.stack.counterOne : copy.stack.counterMany;

  return (
    <section className="diag-step diag-step-stack" aria-labelledby="diag-stack-heading">
      <header className="diag-step-head">
        <div className="diag-step-headrow">
          <h1 id="diag-stack-heading" className="diag-step-title serif">{copy.stack.title}</h1>
          <p className="diag-step-lead">{copy.stack.lead}</p>
        </div>
      </header>

      <div className="diag-step-body diag-stack-body">
        {/* Custom tool — moved to TOP */}
        <div className="diag-stack-custom-top">
          <div className="diag-stack-custom-text">
            <span className="diag-stack-custom-label mono">{copy.stack.customLabel}</span>
            <span className="diag-stack-custom-sub">{copy.stack.customSub}</span>
          </div>
          <div className="diag-stack-custom-row">
            <input
              type="text"
              placeholder={copy.stack.customPlaceholder}
              value={form.customTool}
              onChange={(e) => setForm({ ...form, customTool: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTool(); } }}
              className="diag-input diag-stack-custom-input"
            />
            <button
              type="button"
              onClick={addCustomTool}
              disabled={!(form.customTool || '').trim()}
              className="diag-stack-custom-btn"
            >
              <IconPlus size={12} />
              <span>{copy.stack.customAdd}</span>
            </button>
          </div>
        </div>

        {/* Search + counter */}
        <div className="diag-stack-controls">
          <input
            type="text"
            placeholder={copy.stack.search}
            value={toolSearch}
            onChange={(e) => setToolSearch(e.target.value.toLowerCase())}
            className="diag-input diag-stack-search"
          />
          <span className="diag-stack-counter mono">
            <span className="diag-stack-counter-num">{counter}</span> {counterLabel}
          </span>
        </div>

        {/* Tool catalog (scrollable) */}
        <div className="diag-stack-scroll">
          {TOOL_CATALOG.map(group => {
            const filtered = group.tools.filter(t => t.toLowerCase().includes(toolSearch));
            if (filtered.length === 0 && toolSearch) return null;
            const isPopulares = group.group === 'Populares';
            const groupSelected = form.tools.filter(t => group.tools.includes(t)).length;
            return (
              <div key={group.group} className={`diag-stack-group ${isPopulares ? 'is-populares' : ''}`}>
                <div className="diag-stack-group-head mono">
                  <span>{group.group}</span>
                  {groupSelected > 0 ? <span className="diag-stack-group-count">{groupSelected} {counterLabel}</span> : null}
                </div>
                <div className="diag-stack-tiles">
                  {filtered.map(tool => {
                    const selected = form.tools.includes(tool);
                    const domain = TOOL_DOMAINS[tool];
                    return (
                      <button
                        key={`${group.group}-${tool}`}
                        type="button"
                        onClick={() => toggleTool(tool)}
                        className={`diag-tile ${selected ? 'is-selected' : ''}`}
                        aria-pressed={selected}
                      >
                        {domain ? (
                          <img
                            src={buildLogoUrl(domain, { size: 64 })}
                            alt=""
                            className="diag-tile-logo"
                            onError={(e) => { e.target.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`; }}
                          />
                        ) : <span className="diag-tile-logo diag-tile-logo-fallback" aria-hidden="true">·</span>}
                        <span className="diag-tile-name">{tool}</span>
                        {selected ? <span className="diag-tile-check"><IconCheck size={11} /></span> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <footer className="diag-step-actions">
        <button type="button" onClick={onBack} className="diag-cta-ghost">
          <IconArrow size={12} dir="left" />
          <span>{copy.stack.ctaBack}</span>
        </button>
        <button type="button" onClick={onContinue} className="diag-cta-primary">
          <span>{copy.stack.ctaNext}</span>
          <IconArrow size={12} />
        </button>
      </footer>
    </section>
  );
}

/* ---------- TEASER STEP ----------
   Step 3: shows the AI-generated 8-slide teaser deck inline (iframe srcDoc) before
   the user reaches the booking calendar. POSTs the merged form payload to
   /api/generate-teaser on mount. AbortController cleanup avoids leaked fetches.

   CSS lives next to the component as a co-located <style> tag (additive-only —
   landing/index.html is locked per CLAUDE.md). Reuses existing CSS tokens
   (--bg, --bg-2, --ink, --rule, --rule-strong, --on-ink) and existing button
   classes (.diag-cta-primary / .diag-cta-ghost) from index.html so the visual
   identity stays consistent. */

const TEASER_STYLES = `
.diag-step-teaser .diag-teaser-loading,
.diag-step-teaser .diag-teaser-error {
  width: 100%;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  background: var(--bg-2);
  border: 1px solid var(--rule);
  border-radius: 12px;
  padding: 32px 24px;
  margin-top: 18px;
  color: var(--ink-2);
}
.diag-step-teaser .diag-teaser-loading-label {
  font-size: 13px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.diag-step-teaser .diag-teaser-loading-bar {
  width: 220px;
  height: 2px;
  background: var(--rule);
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}
.diag-step-teaser .diag-teaser-loading-bar::after {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--ink);
  transform-origin: left center;
  animation: diagTeaserBar 1.6s ease-in-out infinite;
}
@keyframes diagTeaserBar {
  0%   { transform: scaleX(0.05); }
  50%  { transform: scaleX(0.85); }
  100% { transform: scaleX(0.05); }
}
.diag-step-teaser .diag-teaser-error-msg {
  font-size: 14px;
  color: var(--ink-2);
  max-width: 520px;
  line-height: 1.5;
}
.diag-step-teaser .diag-cta-primary[disabled],
.diag-step-teaser .diag-cta-primary[aria-disabled="true"] {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}
.diag-step-teaser .diag-teaser-ready-title {
  font-size: 1.1rem;
  margin: 0 0 24px;
  color: var(--ink);
}
.diag-step-teaser .diag-stat-tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 0 0 28px;
}
.diag-step-teaser .diag-stat-tile {
  padding: 18px 14px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: var(--paper);
}
.diag-step-teaser .diag-stat-value {
  font-family: var(--serif);
  font-size: 1.8rem;
  line-height: 1;
  color: var(--ink);
  font-weight: 400;
}
.diag-step-teaser .diag-stat-unit {
  font-family: var(--mono);
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: var(--ink-3);
  text-transform: uppercase;
  margin: 4px 0 6px;
}
.diag-step-teaser .diag-stat-label {
  font-size: 0.85rem;
  color: var(--ink-2);
}
.diag-step-teaser .diag-teaser-open-cta {
  display: inline-block;
  padding: 14px 22px;
  background: var(--ink);
  color: var(--bg);
  border-radius: 999px;
  text-decoration: none;
  font-weight: 600;
  margin: 0 0 16px;
}
.diag-step-teaser .diag-teaser-disclosure {
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--ink-3);
  background: var(--bg-2);
  border: 1px solid var(--rule);
  border-radius: 999px;
  padding: 8px 16px;
  margin: 0;
  display: inline-block;
}
@media (max-width: 640px) {
  .diag-step-teaser .diag-stat-tiles { grid-template-columns: 1fr; }
  .diag-step-teaser .diag-teaser-disclosure { display: block; text-align: center; }
}
`;

function TeaserStyleTag() {
  return <style dangerouslySetInnerHTML={{ __html: TEASER_STYLES }} />;
}

function TeaserStep({ form, dialect, copy, pricingPhase, onBack, onContinue }) {
  // ── Cache hit short-circuit ────────────────────────────────────────────────
  // If user navigates back into Step 4 with the same form payload, reuse the
  // previously-fetched meta. Avoids wasted Gemini calls + slug churn.
  const cacheKey = teaserCacheKey(form, dialect);
  const cachedTeaser = loadTeaserCache(cacheKey);

  const [teaserState, setTeaserState] = useState(cachedTeaser ? 'ready' : 'generating');
  const [teaserMeta, setTeaserMeta] = useState(cachedTeaser || null);

  useEffect(() => {
    if (teaserState !== 'generating') return;
    const ctrl = new AbortController();

    // Detect localhost (no /api/* on the dev server) OR explicit ?devMockTeaser=1
    // override → return mock meta so engineers can preview the handoff surface.
    let useDevMock = false;
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('devMockTeaser') === '1') useDevMock = true;
      else {
        const host = window.location.hostname;
        if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
          useDevMock = true;
        }
      }
    } catch (e) { /* ignore — fall through to real fetch */ }

    if (useDevMock) {
      const meta = { slug: 'dev-mock', hours_saved: 32, roi_eur: 1840, opportunities_count: 3 };
      setTeaserMeta(meta);
      saveTeaserCache(meta, cacheKey);
      setTeaserState('ready');
      return () => ctrl.abort();
    }

    fetch('/api/generate-teaser', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, dialect, pricing_phase: pricingPhase, signals: form.signals || [], volumes: form.volumes || {}, lever: form.lever || '' }),
      signal: ctrl.signal
    })
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error((j && j.error) || 'Teaser API error');
        const meta = {
          slug: j.slug,
          hours_saved: j.hours_saved,
          roi_eur: j.roi_eur,
          opportunities_count: j.opportunities_count,
        };
        setTeaserMeta(meta);
        saveTeaserCache(meta, cacheKey);
        setTeaserState('ready');
      })
      .catch(e => {
        if (e && e.name === 'AbortError') return;
        console.error('[TeaserStep] generate-teaser failed:', e.message);
        setTeaserState('error');
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teaserState]);

  const t = copy.teaser;
  const formatN = (v) => (typeof v === 'number' ? v.toLocaleString('es-ES') : '—');

  // ── State 1: generating ───────────────────────────────────────────────────
  if (teaserState === 'generating') {
    return (
      <section className="diag-step diag-step-teaser" aria-labelledby="diag-teaser-heading">
        <TeaserStyleTag />
        <header className="diag-step-head">
          <div className="diag-step-headrow">
            <h1 id="diag-teaser-heading" className="diag-step-title serif">{t.title}</h1>
            <p className="diag-step-lead">{t.lead}</p>
          </div>
        </header>
        <div className="diag-step-body diag-teaser-body">
          <div className="diag-teaser-loading" role="status" aria-live="polite">
            <span className="diag-teaser-loading-label mono">{t.loading}</span>
            <span className="diag-teaser-loading-bar" aria-hidden="true" />
          </div>
        </div>
        <footer className="diag-step-actions">
          <button type="button" onClick={onBack} className="diag-cta-ghost">
            <IconArrow size={12} dir="left" />
            <span>{t.back}</span>
          </button>
          <button type="button" className="diag-cta-primary" disabled aria-disabled="true">
            <span>{t.continue}</span>
            <IconArrow size={12} />
          </button>
        </footer>
      </section>
    );
  }

  // ── State 3: error ────────────────────────────────────────────────────────
  if (teaserState === 'error') {
    return (
      <section className="diag-step diag-step-teaser" aria-labelledby="diag-teaser-heading">
        <TeaserStyleTag />
        <header className="diag-step-head">
          <div className="diag-step-headrow">
            <h1 id="diag-teaser-heading" className="diag-step-title serif">{t.title}</h1>
          </div>
        </header>
        <div className="diag-step-body diag-teaser-body">
          <div className="diag-teaser-error" role="alert">
            <p className="diag-teaser-error-msg">{t.error}</p>
          </div>
        </div>
        <footer className="diag-step-actions">
          <button type="button" onClick={onBack} className="diag-cta-ghost">
            <IconArrow size={12} dir="left" />
            <span>{t.back}</span>
          </button>
          <button type="button" onClick={onContinue} className="diag-cta-primary">
            <span>{t.continue}</span>
            <IconArrow size={12} />
          </button>
        </footer>
      </section>
    );
  }

  // ── State 2: ready ────────────────────────────────────────────────────────
  return (
    <section className="diag-step diag-step-teaser" aria-labelledby="diag-teaser-heading">
      <TeaserStyleTag />
      <header className="diag-step-head">
        <div className="diag-step-headrow">
          <h1 id="diag-teaser-heading" className="diag-step-title serif">{t.title}</h1>
          <p className="diag-step-lead">{t.lead}</p>
        </div>
      </header>

      <div className="diag-step-body diag-teaser-body">
        <p className="diag-teaser-ready-title">✓ {t.readyTitle}</p>

        <div className="diag-stat-tiles">
          <div className="diag-stat-tile">
            <div className="diag-stat-value">{formatN(teaserMeta.hours_saved)}</div>
            <div className="diag-stat-unit">h / mes</div>
            <div className="diag-stat-label">{t.hoursLabel}</div>
          </div>
          <div className="diag-stat-tile">
            <div className="diag-stat-value">€{formatN(teaserMeta.roi_eur)}</div>
            <div className="diag-stat-unit">/ mes</div>
            <div className="diag-stat-label">{t.eurosLabel}</div>
          </div>
          <div className="diag-stat-tile">
            <div className="diag-stat-value">{teaserMeta.opportunities_count}</div>
            <div className="diag-stat-label">{t.opportunitiesLabel}</div>
          </div>
        </div>

        <a
          href={`/diagnostico/${encodeURIComponent(teaserMeta.slug)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="diag-teaser-open-cta"
        >
          {t.openReport}
        </a>
        <p className="diag-teaser-disclosure mono" role="note">{t.disclosure}</p>
      </div>

      <footer className="diag-step-actions">
        <button type="button" onClick={onBack} className="diag-cta-ghost">
          <IconArrow size={12} dir="left" />
          <span>{t.back}</span>
        </button>
        <button type="button" onClick={onContinue} className="diag-cta-primary">
          <span>{t.continue}</span>
          <IconArrow size={12} />
        </button>
      </footer>
    </section>
  );
}

/* ---------- HORA STEP ---------- */
function CalendarEmbed({ form, dialect, onBooking }) {
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);
  const website = form.website.trim() ? (form.website.includes('://') ? form.website : 'https://' + form.website) : '';
  const metadata = {
    company: form.company, website, ciudad: form.ciudad, sector: form.sector,
    role: form.role, team_size: form.size, revenue_range: form.revenueRange,
    phone: form.phone, current_stack: form.tools, stack_count: form.tools.length,
    main_pain: form.mainPain, dialect, source: 'enautomatico.pro/diagnostico',
    lever: form.lever || ''
  };
  const notes = `Empresa: ${form.company}\nWeb: ${website}\nCiudad: ${form.ciudad}\nSector: ${form.sector}\nRol: ${form.role}\nEquipo: ${form.size}\nFacturación: ${form.revenueRange}\nTeléfono: ${form.phone}\nStack: ${form.tools.join(', ') || '(sin seleccionar)'}\nPrincipal dolor: ${form.mainPain || '(sin especificar)'}\nObjetivo: ${form.lever || '(sin elegir)'}`;
  const calParams = new URLSearchParams({ name: form.name, email: form.email, notes, metadata: JSON.stringify(metadata) });
  const calUrl = `https://cal.com/sborzone/diagnostico-enautomatico?${calParams}&overlayCalendar=true`;
  const directUrl = `https://cal.com/sborzone/diagnostico-enautomatico`;

  useEffect(() => {
    const handleBookingMessage = (event) => {
      if (event.data?.cal?.action === 'bookingSuccessful') onBooking?.();
    };
    window.addEventListener('message', handleBookingMessage);
    return () => window.removeEventListener('message', handleBookingMessage);
  }, [onBooking]);

  useEffect(() => {
    const t = setTimeout(() => { if (!iframeReady) setIframeFailed(true); }, 10000);
    return () => clearTimeout(t);
  }, [iframeReady]);

  return (
    <div className="diag-cal-wrapper">
      {!iframeReady && !iframeFailed ? (
        <div className="diag-cal-skeleton" aria-hidden="true">
          <div className="diag-cal-skeleton-row" />
          <div className="diag-cal-skeleton-row" style={{ width: '70%' }} />
          <div className="diag-cal-skeleton-row" style={{ width: '85%' }} />
          <div className="diag-cal-skeleton-spinner mono">cargando…</div>
        </div>
      ) : null}
      <iframe
        src={calUrl}
        className="diag-cal-iframe"
        title="Calendario de reserva"
        onLoad={() => setIframeReady(true)}
      />
    </div>
  );
}

function HoraStep({ form, dialect, copy, onBack, onBookingSuccess }) {
  const stackPreview = form.tools.slice(0, 6);
  const moreCount = Math.max(0, form.tools.length - stackPreview.length);
  return (
    <section className="diag-step diag-step-hora" aria-labelledby="diag-hora-heading">
      <header className="diag-step-head">
        <div className="diag-step-headrow">
          <h1 id="diag-hora-heading" className="diag-step-title serif">{copy.hora.title}</h1>
        </div>
      </header>

      <div className="diag-step-body diag-hora-body">
        <aside className="diag-stub" aria-label="Resumen de la reserva">
          <div className="diag-stub-head mono">A nombre de</div>
          <div className="diag-stub-name serif">{form.name}</div>
          <div className="diag-stub-company">{form.company}</div>
          <div className="diag-stub-rule" />
          <dl className="diag-stub-meta">
            <div>
              <dt className="mono">{copy.hora.stubFor}</dt>
              <dd>{form.email}</dd>
            </div>
            <div>
              <dt className="mono">{copy.hora.stubStack}</dt>
              <dd>
                {stackPreview.length === 0
                  ? <span className="diag-stub-empty">(sin marcar)</span>
                  : <span>{stackPreview.join(' · ')}{moreCount ? <span className="diag-stub-more"> +{moreCount}</span> : null}</span>}
              </dd>
            </div>
            <div>
              <dt className="mono">{copy.hora.stubDialect}</dt>
              <dd>{dialect === 'rioplatense' ? 'Rioplatense' : 'Castellano'}</dd>
            </div>
            <div>
              <dt className="mono">{copy.hora.stubPlaza}</dt>
              <dd className="diag-stub-plaza"><span className="diag-stub-plaza-dot" /> {copy.hora.stubPlazaPilot}</dd>
            </div>
          </dl>
        </aside>
        <div className="diag-cal">
          <CalendarEmbed form={form} dialect={dialect} onBooking={onBookingSuccess} />
        </div>
      </div>

      <footer className="diag-step-actions">
        <button type="button" onClick={onBack} className="diag-cta-ghost">
          <IconArrow size={12} dir="left" />
          <span>{copy.hora.ctaBack}</span>
        </button>
        <span className="diag-step-hora-hint mono">Cuando elijas una hora, te confirmamos por correo.</span>
      </footer>
    </section>
  );
}

/* ---------- RETURNING BANNER ---------- */
function ReturningBanner({ copy, onReset, onDismiss }) {
  return (
    <div className="diag-returning">
      <div className="diag-returning-inner">
        <span className="diag-returning-text">{copy.chrome.returning}</span>
        <button type="button" onClick={onReset} className="diag-returning-link">{copy.chrome.returningReset}</button>
        <button type="button" onClick={onDismiss} className="diag-returning-close" aria-label="Cerrar">×</button>
      </div>
    </div>
  );
}

/* ---------- DIAGNOSTICO PAGE ---------- */
export function DiagnosticoPage({ dialect }) {
  const copy = useMemo(() => getCopy(dialect), [dialect]);

  // Restore from localStorage on mount.
  const initialDraft = typeof window !== 'undefined' ? loadDraft() : null;
  const [form, setForm] = useState(() => {
    if (initialDraft && initialDraft.form) {
      // Backward-compat: old drafts without signals/volumes/lever hydrate to empty defaults.
      return {
        ...initialDraft.form,
        signals: Array.isArray(initialDraft.form.signals) ? initialDraft.form.signals : [],
        volumes: (typeof initialDraft.form.volumes === 'object' && initialDraft.form.volumes !== null && !Array.isArray(initialDraft.form.volumes))
          ? initialDraft.form.volumes
          : {},
        lever: typeof initialDraft.form.lever === 'string' ? initialDraft.form.lever : '',
      };
    }
    return {
      name: '', email: '',
      phone: dialect === 'rioplatense' ? '+54' : '+34',
      phoneCountry: dialect === 'rioplatense' ? 'AR' : 'ES',
      company: '', website: '', ciudad: '', sector: '', role: '', size: '',
      revenueRange: '', mainPain: '', tools: [], customTool: '',
      signals: [], volumes: {}, lever: ''
    };
  });
  const [step, setStep] = useState(() => (initialDraft && typeof initialDraft.step === 'number' ? initialDraft.step : 0));
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [showReturning, setShowReturning] = useState(() => !!(initialDraft && initialDraft.form && initialDraft.form.email));
  const stepBodyRef = useRef(null);

  // Persist form, debounced.
  useEffect(() => {
    const t = setTimeout(() => saveDraft(form, step), 500);
    return () => clearTimeout(t);
  }, [form, step]);

  // Title
  useEffect(() => {
    const prev = document.title;
    document.title = copy.metaTitle;
    return () => { document.title = prev; };
  }, [copy.metaTitle]);

  // Scroll to top on step change
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const missingFields = getMissingFields(form);
  const formValid = missingFields.length === 0;
  const missingCount = missingFields.length;

  function handleReset() {
    clearDraft();
    setForm({
      name: '', email: '',
      phone: dialect === 'rioplatense' ? '+54' : '+34',
      phoneCountry: dialect === 'rioplatense' ? 'AR' : 'ES',
      company: '', website: '', ciudad: '', sector: '', role: '', size: '',
      revenueRange: '', mainPain: '', tools: [], customTool: '',
      signals: [], volumes: {}, lever: ''
    });
    setTouched({});
    setAttempted(false);
    setStep(0);
    setShowReturning(false);
  }

  function handleBookingSuccess() {
    clearDraft();
    navigateTo('/diagnostico/confirmado');
  }

  function goToStep(n) {
    if (n < 0 || n > 4) return;
    if (n > 0 && !formValid) return; // datos is the gate for every step beyond
    setStep(n);
  }

  // Pricing phase for the teaser CTA. Locked to "gratis" per CLAUDE.md
  // (landing scarcity badge: `Quedan 3 plazas gratis`). URL override via
  // ?phase=99|299 supports preview when the phase advances.
  const pricingPhase = useMemo(() => {
    if (typeof window === 'undefined') return 'gratis';
    try {
      const sp = new URLSearchParams(window.location.search);
      const p = (sp.get('phase') || '').toLowerCase();
      if (p === '99') return '99';
      if (p === '299') return '299';
    } catch (e) {}
    return 'gratis';
  }, []);

  return (
    <div className="diag-page">
      <TopBar copy={copy} />
      {showReturning ? (
        <ReturningBanner copy={copy} onReset={handleReset} onDismiss={() => setShowReturning(false)} />
      ) : null}
      <Stepper currentStep={step} datosValid={formValid} copy={copy} onStepClick={goToStep} />
      <main className="diag-main" ref={stepBodyRef}>
        {step === 0 ? (
          <DatosStep
            form={form}
            setForm={setForm}
            touched={touched}
            setTouched={setTouched}
            attempted={attempted}
            setAttempted={setAttempted}
            copy={copy}
            dialect={dialect}
            onContinue={() => setStep(1)}
            missingCount={missingCount}
            formValid={formValid}
          />
        ) : null}
        {step === 1 ? (
          <ProblemasStep
            form={form}
            setForm={setForm}
            copy={copy}
            dialect={dialect}
            onBack={() => setStep(0)}
            onContinue={() => setStep(2)}
          />
        ) : null}
        {step === 2 ? (
          <StackStep
            form={form}
            setForm={setForm}
            copy={copy}
            onBack={() => setStep(1)}
            onContinue={() => setStep(3)}
          />
        ) : null}
        {step === 3 ? (
          <TeaserStep
            form={form}
            dialect={dialect}
            copy={copy}
            pricingPhase={pricingPhase}
            onBack={() => setStep(2)}
            onContinue={() => setStep(4)}
          />
        ) : null}
        {step === 4 ? (
          <HoraStep
            form={form}
            dialect={dialect}
            copy={copy}
            onBack={() => setStep(3)}
            onBookingSuccess={handleBookingSuccess}
          />
        ) : null}
      </main>
    </div>
  );
}

/* ---------- CONFIRMADO PAGE ---------- */
export function DiagnosticoConfirmado({ dialect }) {
  const copy = useMemo(() => getCopy(dialect), [dialect]);
  useEffect(() => {
    const prev = document.title;
    document.title = (dialect === 'rioplatense' ? 'Reserva confirmada — EnAutomático' : 'Reserva confirmada — EnAutomático');
    return () => { document.title = prev; };
  }, [dialect]);

  return (
    <div className="diag-page diag-confirmado">
      <TopBar dialect={dialect} onShare={() => {}} copy={copy} share={{ shared: false }} />
      <main className="diag-main">
        <div className="diag-confirm-wrap">
          <div className="diag-confirm-stub">
            <div className="diag-confirm-stub-stamp" aria-hidden="true">
              <span className="diag-confirm-stamp-text">{copy.confirmado.stamp}</span>
            </div>
            <div className="diag-section-label">
              <span className="num mono">§ 00</span>
              <span className="name">Reserva</span>
              <span className="line" />
            </div>
            <h1 className="diag-confirm-title serif">{copy.confirmado.title}</h1>
            <p className="diag-confirm-body">{copy.confirmado.body}</p>
          </div>
          <div className="diag-confirm-next">
            <h2 className="diag-confirm-next-title mono">{copy.confirmado.next}</h2>
            <ol className="diag-confirm-steps">
              <li><span className="mono">01</span><p>{copy.confirmado.step1}</p></li>
              <li><span className="mono">02</span><p>{copy.confirmado.step2}</p></li>
              <li><span className="mono">03</span><p>{copy.confirmado.step3}</p></li>
            </ol>
          </div>
          <div className="diag-confirm-actions">
            <button type="button" onClick={() => navigateTo('/')} className="diag-confirm-btn diag-confirm-btn-primary">
              {copy.confirmado.backHome}
              <IconArrow size={12} />
            </button>
            <button type="button" onClick={() => navigateTo('/diagnostico')} className="diag-confirm-btn diag-confirm-btn-ghost">
              {copy.confirmado.bookAgain}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
